<script setup lang="ts">
export interface Props {
  title?: string
  content?: string | number | null
  clickable?: boolean
  variation?: number | null
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  content: null,
  clickable: false,
  variation: null
})

const labelSr = computed(() => {
  if (props.variation == null) return ''
  if (props.variation > 0) return 'Increased by'
  if (props.variation < 0) return 'Decreased by'
  return 'No variation'
})

const variationFormatted = computed(() => {
  if (props.variation == null) return ''
  return `${(props.variation > 0 ? '+' : '') + props.variation}%`
})

const variationIcon = computed(() => {
  if (props.variation == null) return ''
  if (props.variation > 0) {
    return 'M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z'
  }
  if (props.variation < 0) {
    return 'M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z'
  }
  return ''
})

const variationColor = computed(() => {
  if (props.variation == null) return 'gray'
  if (props.variation > 0) return 'green'
  if (props.variation < 0) return 'red'
  return 'gray'
})
</script>

<template>
  <div
    class="overflow-hidden rounded-xl border bg-white duration-300"
    :class="{ 'cursor-pointer hover:bg-muted/50': clickable }"
  >
    <div class="p-5">
      <slot name="title">
        <dt class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
          {{ title }}
        </dt>
      </slot>
      <slot name="content">
        <dd class="mt-1 flex items-baseline justify-between text-3xl font-extrabold text-primary">
          <div v-if="content != null">{{ content }}</div>
          <slot v-else />
          <div
            v-if="variation != null"
            class="inline-flex items-baseline rounded-full px-2.5 py-0.5 text-sm font-medium md:mt-2 lg:mt-0"
            :class="
              variation == 0
                ? 'bg-muted text-foreground'
                : variation > 0
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
            "
          >
            <svg
              class="-ml-1 mr-0.5 h-5 shrink-0 self-center"
              :class="(variationIcon == '' ? 'w-2' : 'w-5') + ' text-' + variationColor + '-500'"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fill-rule="evenodd" :d="variationIcon" clip-rule="evenodd" />
            </svg>
            <span v-if="labelSr" class="sr-only">{{ labelSr }}</span>
            {{ variationFormatted }}
          </div>
        </dd>
      </slot>
      <slot name="bottom" />
    </div>
  </div>
</template>
